import { useCallback, useState } from "react";
import { AxiosRequest } from "_services/api";
import { HttpResponse } from "_services/api/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IData } from "_services/api/interface/HttpResponse";
import { converterDataEua } from "_utils/Data";
import { IFiltroPagamentoTaxa } from "../interfaces/IFiltroPagamentoTaxa";
import { IListarLotePagamento } from "../interfaces/IListarLotePagamento";

export function usePagamentoTaxa() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [filtro, setFiltro] = useState<IFiltroPagamentoTaxa>({});
  const [dadosLotes, setDadosLotes] = useState<IListarLotePagamento[]>([]);

  const montarQueryParams = (
    filtros: IFiltroPagamentoTaxa,
    pagina: number,
    limite: number,
    coluna?: string,
    direcao?: string,
  ) => {
    const params = new URLSearchParams();
    params.append("pagina", String(pagina));
    params.append("limite", String(limite));

    if (filtros.status !== null && filtros.status !== undefined) {
      params.append("status", String(filtros.status));
    }

    if (filtros.idUsuario) params.append("idUsuario", String(filtros.idUsuario));
    if (filtros.idEmpresa) params.append("idEmpresa", String(filtros.idEmpresa));
    if (filtros.idsLotes?.length) {
      params.append("lotes", JSON.stringify(filtros.idsLotes));
    }
    
    if (filtros.idsProcessos?.length) {
      params.append("processos", JSON.stringify(filtros.idsProcessos));
    }
    
    if (filtros.dadosVeiculos?.length) {
      params.append("veiculos", JSON.stringify(filtros.dadosVeiculos));
    }
    
    if (
      filtros.dataCadastro?.length === 2 &&
      filtros.dataCadastro[0] &&
      filtros.dataCadastro[1]
    ) {
      params.append("dataInicio", converterDataEua(filtros.dataCadastro[0]) ?? "");
      params.append("dataFim", converterDataEua(filtros.dataCadastro[1]) ?? "");
    }
    
    if (coluna && direcao) {
      params.append("orderBy", coluna);
      params.append("orderDirecao", direcao.toUpperCase());
    }
  
    return params.toString();
  };

  const listarLotes = useCallback(
    async (
      dadosFiltros?: IFiltroPagamentoTaxa,
      paginaAtual = page,
      limitAtual = limit,
      coluna?: string,
      direcao?: string,
    ) => {
      setLoading(true);
      const filtros = dadosFiltros ?? filtro;
      if (dadosFiltros) {
        setFiltro(dadosFiltros);
      }

      try {
        const query = montarQueryParams(
          filtros,
          paginaAtual,
          limitAtual,
          coluna,
          direcao,
        );
        const { status, data } = await msAw.get(
          `/financeiro/pagamento-taxa/lote?${query}`,
        );

        setDadosLotes(data?.itens ?? []);
        setTotalRows(data?.meta?.total ?? 0);
        return new HttpResponse(false, status, data);
      } catch (error: any) {
        // 404 = "Nenhum lote encontrado" (padrão do ms) -> lista vazia, sem erro de tela
        setDadosLotes([]);
        setTotalRows(0);
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, filtro],
  );

  const relatorioLotes = async () => {
    // limite=0 -> backend retorna sem paginação (todos os lotes do filtro atual).
    try {
      const query = montarQueryParams(filtro, 1, 0);
      const { status, data } = await msAw.get(
        `/financeiro/pagamento-taxa/lote?${query}`,
      );
      return new HttpResponse(false, status, { itens: data?.itens ?? [] });
    } catch (error: any) {
      return new HttpError<IData>(true, error);
    }
  };

  const excluirLote = async (idLote: number) => {
    try {
      const { status, data } = await msAw.delete(
        `/financeiro/pagamento-taxa/lote/${idLote}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: any) {
      return new HttpError<IData>(true, error);
    }
  };

  const validarProcessos = async (processos: string[]) => {
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: any) {
      return new HttpError<IData>(true, error);
    }
  };

  const validarVeiculos = async (placasChassisRenavams: string[]) => {
    try {
      const { status, data } = await msAw.get(
        `/processo/veiculo/validar/?placasChassisRenavams=${JSON.stringify(placasChassisRenavams)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: any) {
      return new HttpError<IData>(true, error);
    }
  };

  return {
    loading,
    page,
    limit,
    totalRows,
    filtro,
    dadosLotes,
    setPage,
    setLimit,
    setFiltro,
    listarLotes,
    relatorioLotes,
    excluirLote,
    validarProcessos,
    validarVeiculos,
  };
}
